import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AuthLayout } from "@/components/layouts/auth-layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "../hooks/use-auth";
import { changePassword } from "../api/auth-api";

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { clearRequiresPasswordChange, isAuthenticated, user } = useAuth();
  const forcedChange = user?.requiresPasswordChange === true;
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      clearRequiresPasswordChange();
      toast.success("Password updated");
      navigate("/schedules", { replace: true });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update password";
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isAuthenticated) {
      navigate("/schedules");
    } else {
      navigate("/login");
    }
  };

  return (
    <AuthLayout>
      <div className="bg-surface border border-border-subtle rounded-lg p-6">
        <h1 className="text-lg font-semibold text-white">Change password</h1>
        <p className="text-xs text-text-muted mt-1 mb-5">
          Choose a new password for your account
        </p>
        <form onSubmit={handleSubmit} noValidate>
          {!forcedChange && (
            <div className="mb-3.5">
              <Label htmlFor="cp-current">Current password</Label>
              <Input
                id="cp-current"
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="current-password-input"
              />
            </div>
          )}
          <div className="mb-3.5">
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
            />
          </div>
          <div className="mb-4">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="confirm-password-input"
            />
          </div>
          {error && (
            <div
              role="alert"
              data-testid="form-error"
              className="mb-3 px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs"
            >
              {error}
            </div>
          )}
          <div className="flex gap-2">
            {!forcedChange && (
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={handleCancel}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              className={forcedChange ? "w-full" : "flex-1"}
              isLoading={submitting}
              data-testid="submit-button"
            >
              Update password
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
}
